import { createContext, useContext, useState } from 'react';

import { FCWithChildren } from 'common/types';

import type {
  PaymentCardOrderFlowContext,
  PaymentCardOrderFlowSharedState,
} from '../../types';

// Context
export interface PhysicalOrderFlowConfig {
  cardType: 'basic' | 'premium';
  canSelectDeliveryAddress: boolean;
  coordinates: {
    fullName: string;
    personalAddress: {
      street: string;
      streetAddition: string;
      city: string;
      zip: string;
    };
    professionalAddress: {
      street: string;
      city: string;
      zip: string;
    };
  };
  bankAccount: {
    bankAccountId: string;
    nextBalance: number;
  };
  pricingWithoutVAT: {
    cardPrice: number;
    monthlyFee: number;
  };
}

export interface PhysicalOrderFlowSharedState
  extends PaymentCardOrderFlowSharedState {
  agreedToInsurancePolicy: boolean | undefined;
  pin: string;
  pinConfirmationWasDifferent: boolean;
  isPasscodeShown: boolean;
  shouldSendToCompanyAddress: boolean | undefined;
  createdCardId: string | undefined;
}

type PhysicalCardOrderFlowContext = PaymentCardOrderFlowContext<
  PhysicalOrderFlowConfig,
  PhysicalOrderFlowSharedState
>;

const PHYSICAL_CARD_ORDER_FLOW_CONTEXT = createContext<
  PhysicalCardOrderFlowContext | undefined
>(undefined);

// Provider
interface ProviderProps {
  value: PhysicalOrderFlowConfig;
}

const INITIAL_SHARED_STATE: PhysicalOrderFlowSharedState = {
  agreedToInsurancePolicy: undefined,
  alias: undefined,
  createdCardId: undefined,
  isPasscodeShown: false,
  pin: '',
  pinConfirmationWasDifferent: false,
  shouldSendToCompanyAddress: undefined,
};

export const PhysicalCardOrderFlowContextProvider: FCWithChildren<
  ProviderProps
> = (props) => {
  const { children, value: flowConfig } = props;

  const [sharedState, setSharedState] = useState(INITIAL_SHARED_STATE);

  return (
    <PHYSICAL_CARD_ORDER_FLOW_CONTEXT.Provider
      value={{
        flowConfig,
        setSharedState,
        sharedState,
      }}
    >
      {children}
    </PHYSICAL_CARD_ORDER_FLOW_CONTEXT.Provider>
  );
};

// Hook
export function usePhysicalCardOrderFlowContext() {
  const context = useContext(PHYSICAL_CARD_ORDER_FLOW_CONTEXT);
  if (!context) {
    throw new Error(
      '"usePhysicalCardOrderFlowContext" can only be used under PhysicalCardOrderFlowContextProvider',
    );
  }
  return context;
}
